import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/browser';
import App from './App.tsx';
import { PasswordGate } from './components/PasswordGate.tsx';
import './index.css';
import { isNative } from './lib/platform';
import { IAP_PRODUCTS } from './lib/iap/products';
import { initPostHog } from './lib/posthog';
import { initErrorTracking } from './lib/errorTracking';
import { initWebVitals } from './lib/webVitals';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (sentryDsn) {
  Sentry.init({
    dsn:              sentryDsn,
    environment:      import.meta.env.MODE,
    tracesSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      // Don't send events in development
      if (import.meta.env.DEV) return null;
      return event;
    },
  });
}

initPostHog();
initErrorTracking();
initWebVitals();

// Prevent the browser from restoring a previous scroll position on page load.
if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

// ─── Native bootstrap ────────────────────────────────────────────────────────

async function initNative(): Promise<void> {
  if (!isNative) return;

  const [
    { StatusBar, Style },
    { SplashScreen },
    { Keyboard },
  ] = await Promise.all([
    import('@capacitor/status-bar'),
    import('@capacitor/splash-screen'),
    import('@capacitor/keyboard'),
  ]);

  // Status bar
  await StatusBar.setStyle({ style: Style.Dark });
  await StatusBar.setBackgroundColor({ color: '#0F1123' });

  // Keyboard, push body up when keyboard appears
  await Keyboard.addListener('keyboardWillShow', info => {
    document.body.style.paddingBottom = `${info.keyboardHeight}px`;
  });
  await Keyboard.addListener('keyboardWillHide', () => {
    document.body.style.paddingBottom = '0px';
  });

  // Splash screen, hide after app is ready
  await SplashScreen.hide({ fadeOutDuration: 400 });
}

// ─── IAP initialisation (native only) ───────────────────────────────────────

async function initIAP(): Promise<void> {
  if (!isNative) return;
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor' as never) as unknown as {
      Purchases: {
        configure: (opts: { apiKey: string }) => Promise<void>;
        addCustomerInfoUpdateListener: (fn: (info: unknown) => void) => Promise<unknown>;
      };
    };

    const rcKey = import.meta.env.VITE_REVENUECAT_API_KEY ?? '';
    if (!rcKey) return;

    await Purchases.configure({ apiKey: rcKey });

    // Log registered products for traceability
    console.debug('[IAP] Registered products:', Object.values(IAP_PRODUCTS));
  } catch { /* RevenueCat not available in dev/web */ }
}

// ─── Mount React then run native bootstrap ───────────────────────────────────

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(
  <React.StrictMode>
    <PasswordGate>
      <App />
    </PasswordGate>
  </React.StrictMode>,
);

// ─── Service Worker (web only) ───────────────────────────────────────────────

function registerSW(): void {
  if (isNative) return;
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* SW optional */ });
  });
}

registerSW();

// Run after React has painted the first frame
requestAnimationFrame(() => {
  Promise.all([initNative(), initIAP()]).catch(console.warn);
});
